import { FEATURE_FLAG_KEYS } from '@alpha-core/feature-flags/feature-flag.constants';
import { ProjectService } from '@alpha-core/platform-ui';
import {
    ADMIN_INBOX_CONFIGS,
    AdminFilter,
    FetchAdminFilterById,
    ICreateFilter,
    INBOX_LIST_TYPE,
    InboxFilterType,
    ListAdminFilters,
} from '@alpha-core/platform-ui/interfaces/admin-filter.interface';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FeatureFlagsService } from '@neutrinos/ngx-feature-flags';
import { MenuItem, MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, catchError, finalize, lastValueFrom, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AdminFilterService {
    private messageService = inject(MessageService);
    private httpClient = inject(HttpClient);
    private projectService = inject(ProjectService);
    private featureFlagsService = inject(FeatureFlagsService);

    private breadcrumbSource = new BehaviorSubject<MenuItem[]>([]);
    breadcrumb$ = this.breadcrumbSource.asObservable();
    private baseUrl = `${environment.ALPHA_UI_ADMIN_SERVICE_URL}`;
    adminFilters: Array<ADMIN_INBOX_CONFIGS> = [];
    listFilterCallInstance: Observable<ADMIN_INBOX_CONFIGS[]> | null = null;

    updateBreadcrumb(items: MenuItem[]) {
        this.breadcrumbSource.next(items);
    }

    createFilter(payload: ICreateFilter) {
        return this.httpClient
            .post<AdminFilter>(`${this.baseUrl}/filters/save`, {
                ...this.fetchProjectIdAndTenantId(),
                ...payload,
            })
            .pipe(
                catchError((error: any) => {
                    this.handleCatchException(error);
                    return of(null);
                }),
            );
    }

    handleCatchException(exception: any) {
        const error = exception?.error?.error || exception?.error || {};
        this.messageService.add({
            severity: 'error',
            summary: 'Failed',
            detail: error?.message,
        });
    }

    updateFilter(payload: ICreateFilter) {
        return this.httpClient
            .post<AdminFilter>(`${this.baseUrl}/filters/update`, {
                ...this.fetchProjectIdAndTenantId(),
                ...payload,
            })
            .pipe(
                catchError((error: any) => {
                    this.handleCatchException(error);
                    return of(null);
                }),
            );
    }

    listFilters(filters?: { pageNumber: number; pageSize: number; search: string }) {
        const { projectId } = this.fetchProjectIdAndTenantId();
        return this.httpClient
            .post<ListAdminFilters>(`${this.baseUrl}/filters/list`, {
                projectId,
                ...(filters || {}),
            })
            .pipe(
                catchError((error: any) => {
                    this.handleCatchException(error);
                    return of(null);
                }),
            );
    }

    listAdminFilters(inboxListTypes: INBOX_LIST_TYPE[]) {
        const { projectId } = this.fetchProjectIdAndTenantId();
        return this.httpClient
            .post<ListAdminFilters>(`${this.baseUrl}/filters/list-by-groups-users`, {
                projectId,
                inboxListType: inboxListTypes,
            })
            .pipe(
                catchError((error: any) => {
                    this.handleCatchException(error);
                    return of(null);
                }),
            );
    }

    getFilterById(filterId: string) {
        return this.httpClient
            .post<FetchAdminFilterById>(`${this.baseUrl}/filters/fetch-by-id`, {
                ...this.fetchProjectIdAndTenantId(),
                filterId,
            })
            .pipe(
                catchError((error: any) => {
                    this.handleCatchException(error);
                    return of(null);
                }),
            );
    }

    async fetchAdminListFilters(inboxListTypes: INBOX_LIST_TYPE[]): Promise<ADMIN_INBOX_CONFIGS[]> {
        if (!this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_KEYS.ADMIN_FILTERS)) return [];

        if (this.listFilterCallInstance) {
            return lastValueFrom(this.listFilterCallInstance);
        }

        this.listFilterCallInstance = this.listAdminFilters(inboxListTypes).pipe(
            map((response: any) =>
                (response?.records || []).map((r: any) => ({
                    name: r.filterName,
                    key: r.inboxListType,
                    default: false,
                    filterObject: r.filters,
                    type: InboxFilterType.ADMIN_FILTER,
                    filterId: r.filterId,
                })),
            ),
            tap((filters) => {
                this.adminFilters = filters;
            }),
            catchError((err) => {
                this.listFilterCallInstance = null;
                throw err;
            }),
            finalize(() => {
                this.listFilterCallInstance = null;
            }),
        );

        return lastValueFrom(this.listFilterCallInstance);
    }

    deleteFilter(filterId: string) {
        return this.httpClient
            .post<void>(`${this.baseUrl}/filters/delete`, {
                filterId,
            })
            .pipe(
                catchError((error: any) => {
                    this.handleCatchException(error);
                    return of('error');
                }),
            );
    }

    private fetchProjectIdAndTenantId() {
        const currentProject = this.projectService.fetchCurrentProject();
        const tenant = currentProject.tenant?.name;
        return {
            projectId: currentProject.projectId,
            tenantId: tenant,
        };
    }
}
